/**
 * Modal for unpausing a patient with task date recalculation.
 * @agentic connections
 */

// Components
import { Modal } from "@benchmark/ui/components/atomic";
import { Stack, Button, Group, Text, Alert, Card } from "@mantine/core";
import {
  IconAlertCircle,
  IconPlayerPlay,
  IconInfoCircle,
} from "@tabler/icons-react";

// Hooks
import { useState } from "react";
import { useMedplum } from "@medplum/react";
import { notifications } from "@mantine/notifications";
import { usePatientPauseStatus } from "@/hooks/domain/usePatientPauseStatus";

// Types
import type { Patient } from "@medplum/fhirtypes";

// Workflows
import { unpausePatient } from "@benchmark/core/workflows";

// Interfaces
/**
 * Props for the unpause patient modal.
 * @agentic connections
 */
export type UnpausePatientModalProps = {
  opened: boolean;
  onClose: () => void;
  patient: Patient;
  onSuccess?: () => void;
};

/**
 * Modal for unpausing a patient with task date recalculation.
 * @agentic connections
 */
export const UnpausePatientModal = ({
  opened,
  onClose,
  patient,
  onSuccess,
}: UnpausePatientModalProps) => {
  const medplum = useMedplum();
  const {
    pauseReason,
    remainingDays,
    loading: pauseStatusLoading,
    refetch: refetchPauseStatus,
  } = usePatientPauseStatus(patient);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await unpausePatient(medplum, patient);

      // Refetch pause status to update UI
      await refetchPauseStatus();

      notifications.show({
        title: "Member Unpaused",
        message: `${patient.name?.[0]?.given?.[0] || "Member"} has been unpaused. Task dates have been recalculated.`,
        color: "green",
        icon: <IconPlayerPlay size={16} />,
      });

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error("Error unpausing member:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to unpause member. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const remainingDaysText =
    remainingDays === 1 ? "1 day" : `${remainingDays} days`;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Unpause Member"
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Unpausing this member will resume their care plan activities and
          recalculate task due dates based on the actual pause duration.
        </Text>

        {pauseStatusLoading ? (
          <Text size="sm" c="dimmed">
            Loading pause details...
          </Text>
        ) : (
          <Card withBorder padding="md">
            <Stack gap="xs">
              {pauseReason && (
                <Group gap="xs" align="flex-start">
                  <Text size="sm" fw={500}>
                    Reason:
                  </Text>
                  <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                    {pauseReason}
                  </Text>
                </Group>
              )}
              {remainingDays > 0 && (
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    Remaining:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {remainingDaysText}
                  </Text>
                </Group>
              )}
            </Stack>
          </Card>
        )}

        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Task Date Recalculation"
          color="blue"
          variant="light"
        >
          Task due dates will be adjusted based on the actual pause duration. If
          unpaused early, tasks will be moved forward accordingly.
        </Alert>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            leftSection={<IconPlayerPlay size={16} />}
          >
            Unpause Member
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
